import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { FaChevronLeft, FaChevronRight, FaSyncAlt } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import {
    AccessMode,
    AccessRow,
    KeyCell,
    ModeBadge,
    OUTCOME_LABELS,
    OUTCOME_OPTIONS,
    Outcome,
    OutcomeBadge,
    UNASSIGNED_BATCH,
    accessDetailPath,
    batchLabel,
    formatBytes,
    formatDateTime,
    formatUa,
    handleAdminApiError,
} from './shared'

interface Filters {
    accessMode: AccessMode | 'all'
    outcome: string
    caseRef: string
    ip: string
    accessKeyId: string
    dateFrom: string
    dateTo: string
    search: string
}

const DEFAULT_FILTERS: Filters = {
    accessMode: 'raw',
    outcome: 'all',
    caseRef: '',
    ip: '',
    accessKeyId: '',
    dateFrom: '',
    dateTo: '',
    search: '',
}

interface KeyOption {
    id: number
    label: string
    holder_name: string | null
    enabled: boolean
    system_role: 'dashboard' | 'master' | null
}

const PAGE_SIZES = [25, 50, 100]

interface Summary {
    byOutcome: Record<string, number>
    uniqueIps: number
    uniqueBundles: number
}

/**
 * Level 2: all accesses for a single funding batch (or the "unassigned"
 * bucket). Row click opens the single-access detail page.
 */
export const DocumentBundleAccessBatchView = () => {
    const { batchGroup: batchParam } = useParams<{ batchGroup: string }>()
    const navigate = useNavigate()
    const batchKey = batchParam ? decodeURIComponent(batchParam) : UNASSIGNED_BATCH
    const isUnassigned = batchKey === UNASSIGNED_BATCH

    const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS)
    const [applied, setApplied] = useState<Filters>(DEFAULT_FILTERS)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(25)
    const [rows, setRows] = useState<AccessRow[]>([])
    const [total, setTotal] = useState(0)
    const [summary, setSummary] = useState<Summary>({ byOutcome: {}, uniqueIps: 0, uniqueBundles: 0 })
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [keyOptions, setKeyOptions] = useState<KeyOption[]>([])

    // Keys for the filter dropdown; failure here is non-fatal. The master key
    // is offered via the fixed "master" option (matches by type, so it also
    // covers rows logged before the master key had its own row).
    useEffect(() => {
        api({ url: '/document-access-keys', data: {} })
            .then(({ data }) =>
                setKeyOptions(
                    data?.success ? (data.keys || []).filter((k: KeyOption) => k.system_role !== 'master') : []
                )
            )
            .catch(() => setKeyOptions([]))
    }, [])

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const { data } = await api({
                url: '/document-access-log',
                data: {
                    batchGroup: batchKey,
                    page,
                    limit,
                    accessMode: applied.accessMode,
                    outcome: applied.outcome !== 'all' ? applied.outcome : undefined,
                    caseRef: applied.caseRef || undefined,
                    ip: applied.ip || undefined,
                    accessKeyId: applied.accessKeyId || undefined,
                    dateFrom: applied.dateFrom || undefined,
                    dateTo: applied.dateTo || undefined,
                    search: applied.search || undefined,
                },
            })
            if (!data?.success) throw new Error(data?.message || 'Failed to load accesses')
            setRows(data.rows || [])
            setTotal(Number(data.total) || 0)
            setSummary(data.summary || { byOutcome: {}, uniqueIps: 0, uniqueBundles: 0 })
        } catch (err: any) {
            if (handleAdminApiError(err, navigate)) return
            setError(err?.response?.data?.message || err?.message || 'Failed to load accesses')
        } finally {
            setLoading(false)
        }
    }, [applied, batchKey, limit, navigate, page])

    useEffect(() => {
        load()
    }, [load])

    // Outcome chips for the mode currently applied (or all modes).
    const outcomeCounts = useMemo(() => {
        const counts: Partial<Record<Outcome, number>> = {}
        for (const [key, value] of Object.entries(summary.byOutcome || {})) {
            const [mode, outcome] = key.split(':') as [AccessMode, Outcome]
            if (applied.accessMode !== 'all' && mode !== applied.accessMode) continue
            counts[outcome] = (counts[outcome] || 0) + value
        }
        return counts
    }, [summary, applied.accessMode])

    const totalPages = Math.max(1, Math.ceil(total / limit))
    const introducer = rows.find((r) => r.batch_introducer)?.batch_introducer ?? null

    const applyFilters = (next: Filters) => {
        setFilters(next)
        setApplied(next)
        setPage(1)
    }

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        applyFilters(filters)
    }

    const onReset = () => applyFilters(DEFAULT_FILTERS)

    const toggleOutcomeChip = (outcome: string) =>
        applyFilters({ ...filters, outcome: applied.outcome === outcome ? 'all' : outcome })

    const open = (r: AccessRow) => navigate(accessDetailPath(r.id))

    return (
        <div className={styles.wrap}>
            <div className={styles.pageHeader}>
                <div>
                    <h2 className={styles.pageTitle}>
                        {isUnassigned ? (
                            <span className={styles.muted}>{batchLabel(null)}</span>
                        ) : (
                            <span className={styles.refCode}>{batchKey}</span>
                        )}
                    </h2>
                    <div className={styles.headerMeta}>
                        {introducer && <span className={styles.metaText}>{introducer}</span>}
                        {!isUnassigned && (
                            <Link className={styles.metaLink} to={`/funding-batch/${encodeURIComponent(batchKey)}`}>
                                Open funding batch <FaChevronRight size={10} />
                            </Link>
                        )}
                        {isUnassigned && (
                            <span className={styles.metaText}>
                                Requests whose case reference could not be matched to a funding batch.
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.statStrip}>
                <div className={styles.stat}>
                    <span className={styles.statValue}>{total.toLocaleString('en-GB')}</span>
                    <span className={styles.statLabel}>Matching accesses</span>
                </div>
                <div className={styles.stat}>
                    <span className={styles.statValue}>{summary.uniqueIps.toLocaleString('en-GB')}</span>
                    <span className={styles.statLabel}>Unique IPs</span>
                </div>
                <div className={styles.stat}>
                    <span className={styles.statValue}>{summary.uniqueBundles.toLocaleString('en-GB')}</span>
                    <span className={styles.statLabel}>Unique bundles</span>
                </div>
            </div>

            <form className={styles.toolbar} onSubmit={onSubmit}>
                <input
                    className={styles.searchInput}
                    type="search"
                    placeholder="Filter by file, IP, city or country…"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
                <label className={styles.toolbarField}>
                    Mode
                    <select
                        className={styles.toolbarSelect}
                        value={filters.accessMode}
                        onChange={(e) => setFilters({ ...filters, accessMode: e.target.value as Filters['accessMode'] })}
                    >
                        <option value="raw">Raw key only</option>
                        <option value="viewer">Viewer only</option>
                        <option value="all">All modes</option>
                    </select>
                </label>
                <label className={styles.toolbarField}>
                    Outcome
                    <select
                        className={styles.toolbarSelect}
                        value={filters.outcome}
                        onChange={(e) => setFilters({ ...filters, outcome: e.target.value })}
                    >
                        {OUTCOME_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </label>
                <label className={styles.toolbarField}>
                    Access key
                    <select
                        className={styles.toolbarSelect}
                        value={filters.accessKeyId}
                        onChange={(e) => setFilters({ ...filters, accessKeyId: e.target.value })}
                    >
                        <option value="">Any key</option>
                        <option value="master">Master key (legacy)</option>
                        {keyOptions.map((k) => (
                            <option key={k.id} value={String(k.id)}>
                                {k.label}
                                {k.holder_name ? ` (${k.holder_name})` : ''}
                                {k.enabled ? '' : ' – disabled'}
                            </option>
                        ))}
                    </select>
                </label>
                <label className={styles.toolbarField}>
                    Case ref
                    <input
                        className={styles.toolbarInput}
                        type="text"
                        value={filters.caseRef}
                        onChange={(e) => setFilters({ ...filters, caseRef: e.target.value })}
                    />
                </label>
                <label className={styles.toolbarField}>
                    IP address
                    <input
                        className={styles.toolbarInput}
                        type="text"
                        value={filters.ip}
                        onChange={(e) => setFilters({ ...filters, ip: e.target.value })}
                    />
                </label>
                <label className={styles.toolbarField}>
                    From
                    <input
                        className={styles.toolbarInput}
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    />
                </label>
                <label className={styles.toolbarField}>
                    To
                    <input
                        className={styles.toolbarInput}
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    />
                </label>
                <div className={styles.toolbarActions}>
                    <button type="button" className={styles.secondaryBtn} onClick={onReset}>
                        Reset
                    </button>
                    <button type="submit" className={styles.secondaryBtn} disabled={loading}>
                        <FaSyncAlt /> Apply
                    </button>
                </div>
            </form>

            {Object.keys(outcomeCounts).length > 0 && (
                <div className={styles.chipRow}>
                    {(Object.keys(OUTCOME_LABELS) as Outcome[])
                        .filter((o) => outcomeCounts[o])
                        .map((o) => (
                            <button
                                key={o}
                                type="button"
                                className={`${styles.chipBtn} ${applied.outcome === o ? styles.active : ''}`}
                                onClick={() => toggleOutcomeChip(o)}
                                title={applied.outcome === o ? 'Clear this outcome filter' : 'Filter by this outcome'}
                            >
                                <OutcomeBadge outcome={o} />
                                <span className={styles.chipCount}>{outcomeCounts[o]?.toLocaleString('en-GB')}</span>
                            </button>
                        ))}
                </div>
            )}

            {error && (
                <div className={styles.errorBanner}>
                    {error}{' '}
                    <button type="button" className={styles.linkBtn} onClick={load}>
                        Retry
                    </button>
                </div>
            )}

            <p className={styles.resultLine}>
                {loading
                    ? 'Loading…'
                    : total === 0
                      ? 'No accesses'
                      : `Showing ${((page - 1) * limit + 1).toLocaleString('en-GB')}–${Math.min(
                            page * limit,
                            total
                        ).toLocaleString('en-GB')} of ${total.toLocaleString('en-GB')} accesses`}
            </p>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>When</th>
                            <th>Outcome</th>
                            <th>Bundle</th>
                            <th>Key</th>
                            <th>Requester</th>
                            <th>Client</th>
                            <th className={styles.numeric}>Bytes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={7}>
                                    <div className={styles.emptyState}>
                                        {loading ? 'Loading accesses…' : 'No accesses match. Try widening the filters.'}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            rows.map((r) => (
                                <tr
                                    key={r.id}
                                    className={styles.clickable}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => open(r)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            open(r)
                                        }
                                    }}
                                >
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackPrimary}>{formatDateTime(r.accessed_at)}</span>
                                            <span className={styles.stackSecondary}>
                                                <ModeBadge mode={r.access_mode} />
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.stackCell}>
                                            <OutcomeBadge outcome={r.outcome} />
                                            {r.http_status !== null && (
                                                <span className={`${styles.stackSecondary} ${styles.mono}`}>
                                                    HTTP {r.http_status}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackPrimary}>
                                                {r.case_ref ? (
                                                    <span className={styles.refCode}>{r.case_ref}</span>
                                                ) : (
                                                    <span className={styles.muted}>—</span>
                                                )}
                                            </span>
                                            <span className={styles.stackSecondary} title={r.file_name || ''}>
                                                <span className={styles.truncate}>{r.file_name || '—'}</span>
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <KeyCell row={r} />
                                    </td>
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={`${styles.stackPrimary} ${styles.mono}`}>
                                                {r.ip_address || '—'}
                                            </span>
                                            <span className={styles.stackSecondary}>
                                                {r.location || <span className={styles.stackMeta}>Unknown location</span>}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={styles.stackSecondary} title={r.user_agent || ''}>
                                            <span className={styles.truncate}>{formatUa(r.ua)}</span>
                                        </span>
                                    </td>
                                    <td className={styles.numeric}>
                                        {r.access_mode === 'raw' ? formatBytes(r.bytes_sent) : '—'}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {total > 0 && (
                <div className={styles.pager}>
                    <select
                        className={styles.pagerSelect}
                        value={limit}
                        onChange={(e) => {
                            setLimit(Number(e.target.value))
                            setPage(1)
                        }}
                    >
                        {PAGE_SIZES.map((n) => (
                            <option key={n} value={n}>
                                {n} per page
                            </option>
                        ))}
                    </select>
                    <div className={styles.pagerControls}>
                        <button
                            type="button"
                            className={styles.pagerBtn}
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            <FaChevronLeft /> Previous
                        </button>
                        <span className={styles.pagerLabel}>
                            Page {page} of {totalPages}
                        </span>
                        <button
                            type="button"
                            className={styles.pagerBtn}
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            Next <FaChevronRight />
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default DocumentBundleAccessBatchView
